/**
 * Collecting against a customer's unpaid bills (BR-24, P1-BR4).
 *
 * The ageing report above says who owes what and how old it is. This is what
 * was missing underneath it: a way to say they have paid. Without it the page
 * could only ever grow — a customer settling their bill in cash had nowhere to
 * go, so the debt stayed on the report and the money stayed off the books.
 *
 * Payment is against a *bill*, not a customer. A partner who has taken ৳2,000
 * from someone owing two months needs to say which month it clears, because
 * that is what decides whether the older bill stops ageing.
 *
 * The entry lands `pending` like every other figure that reaches a balance
 * (BR-07), and it names the account the money went into — a collection that
 * names no account is money that never reaches the books.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts } from '../finance/api.js';
import { useInvoices, useRecordPayment, type Invoice } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const MONEY_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

function PaymentForm({ invoice, onDone }: { invoice: Invoice; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { data: accounts = [] } = useAccounts();
  const pay = useRecordPayment();

  // Settling the bill in full is the common case, so it is the starting value.
  const [amount, setAmount] = useState(invoice.outstanding);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const defaultAccount = accounts.find((account) => account.type === 'cash') ?? accounts[0];
  const selectedAccount = accountId ?? defaultAccount?.id ?? null;

  const canSubmit = MONEY_DRAFT.test(amount) && selectedAccount !== null && !pay.isPending;

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      await pay.mutateAsync({
        invoiceId: invoice.id,
        date: todayInDhaka(),
        amount,
        accountId: selectedAccount!,
      });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className="mt-3 rounded-lg bg-green-50 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={`paid-${String(invoice.id)}`}>
        {t('receivables.amountPaid')}
      </label>
      <input
        id={`paid-${String(invoice.id)}`}
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 tabular-nums"
      />
      {/* The ceiling, in front of the person typing. The server refuses more
          than this rather than absorbing it (AP-1). */}
      <p className="mt-1 text-xs text-gray-600">
        {t('receivables.outstandingIs', { amount: formatMoney(invoice.outstanding) })}
      </p>
      {fieldErrors['amount'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['amount']}</p>}

      <fieldset className="mt-3">
        <legend className="mb-2 text-xs font-medium text-gray-700">{t('receivables.paidInto')}</legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={selectedAccount === account.id}
              className={`min-h-touch rounded-full px-3 py-1.5 text-sm ${
                selectedAccount === account.id
                  ? 'bg-green-700 font-medium text-white'
                  : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
        {fieldErrors['accountId'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['accountId']}</p>
        )}
      </fieldset>

      {errorKey !== null && (
        <div role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(pay.isPending ? 'receivables.saving' : 'receivables.recordPayment')}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-touch rounded-lg bg-white px-3 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

function InvoiceRow({ invoice, canCollect }: { invoice: Invoice; canCollect: boolean }): JSX.Element {
  const { t } = useTranslation();
  const [isPaying, setIsPaying] = useState(false);

  const settled = invoice.status === 'paid';

  return (
    <li className="border-b border-gray-100 py-3 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-800">{invoice.month.slice(0, 7)}</p>
          <p className="text-xs text-gray-500">
            {t('receivables.billedLitres', { litres: invoice.litres })}
            {invoice.paid !== '0.00' && ` · ${t('receivables.alreadyPaid', { amount: formatMoney(invoice.paid) })}`}
          </p>
        </div>
        <span className={`shrink-0 tabular-nums font-medium ${settled ? 'text-gray-400' : 'text-gray-900'}`}>
          {formatMoney(invoice.outstanding)}
        </span>
      </div>

      {/* A settled bill gets no form: there is nothing left to collect, and the
          server would refuse it anyway. */}
      {canCollect && !settled && !isPaying && (
        <button
          type="button"
          onClick={() => setIsPaying(true)}
          className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
        >
          {t('receivables.recordPayment')}
        </button>
      )}

      {canCollect && !settled && isPaying && <PaymentForm invoice={invoice} onDone={() => setIsPaying(false)} />}
    </li>
  );
}

export function InvoicePaymentPanel({
  customerId,
  canCollect,
}: {
  customerId: number;
  canCollect: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { data: invoices = [], isLoading, isError, refetch } = useInvoices({ customerId });

  // A settled bill is history; the panel is opened to collect, not to browse.
  const unpaid = invoices.filter((invoice) => invoice.status !== 'paid');

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      {isLoading && (
        <p role="status" className="py-3 text-center text-sm text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="mb-1">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {!isLoading && unpaid.length === 0 && (
        <p className="py-3 text-center text-sm text-gray-500">{t('receivables.noBills')}</p>
      )}

      {unpaid.length > 0 && (
        <ul>
          {unpaid.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} canCollect={canCollect} />
          ))}
        </ul>
      )}
    </div>
  );
}
